import * as d3 from 'd3';
import { MeasureUnitInfo } from '../../measure-unit/MeasureUnitInfo';
import { BarChartConfig } from '../data/BarChartConfig';
import { LineChartConfig } from '../data/LineChartConfig';
import { ChartDataType } from '../data/ChartDataType';
import { MeasureUnitUtil } from '../../measure-unit/MeasureUnitUtil';
import { MeasureUnitCode } from '../../measure-unit/MeasureUnitCode';
import { ChartRendererContext } from './ChartRendererContext';
import { BandChartData } from 'components/chart/data/BandChartData';

export class LinearAxisRenderer {
  public static TICK_SIZE = 5;

  constructor(
    private gElement: d3.Selection<SVGGElement, unknown, null, undefined>,
    private context: ChartRendererContext,
    private config: LineChartConfig | BarChartConfig,
    private isSecondary: boolean,
    private hasSecondary: boolean
  ) {}

  public renderY(
    chartWidth: number,
    height: number,
    limit?: number
  ): {
    scale: d3.ScaleLinear<number, number>;
    width: number;
    formatter: (d: number) => string;
  } {
    const unit =
      this.isSecondary && this.config.additionalValue?.unit
        ? this.config.additionalValue.unit
        : (this.config.value?.unit ?? MeasureUnitCode.QUANTITY);

    let unitInfo = typeof unit === 'string' ? new MeasureUnitInfo(unit) : unit;

    if (this.config.breakdownMode === 'fill-stacked' || unitInfo.code === MeasureUnitCode.PERCENT) {
      unitInfo = new MeasureUnitInfo(MeasureUnitCode.PERCENT);
    }

    // if data.length is zero we need to include zero otherwise min/max
    // is the same and there will be no scale
    // includeZero = includeZero || data.length === 1;

    const y: d3.ScaleLinear<number, number> = d3.scaleLinear().rangeRound([height, 0]);

    const yAxis = this.isSecondary ? d3.axisRight(y) : d3.axisLeft(y);

    const { step, minStep, maxStep, tickCount } = this.calcTickConfig(unitInfo.code, height, limit);

    // hint: if all data points have the same val min is equals to max
    // what causes provlems in the step generation
    // we need to handle that here!

    y.domain([minStep, maxStep]).nice(tickCount);

    let maxLabelWidth = 0;

    const formatter = this.createValueFormatter(unitInfo, step, maxStep, this.config.breakdownMode === 'fill-stacked');

    if (!this.config.yAxisConfig.hide) {
      yAxis
        .tickFormat(d => formatter(d.valueOf()))
        .tickSize(this.hasSecondary ? -LinearAxisRenderer.TICK_SIZE : -chartWidth);

      let yAxisElement = this.gElement
        .append('g')
        .attr('class', 'axis axis--y')
        .call(yAxis.ticks(tickCount))
        .call(g => {
          g.selectAll('text').each((_, i, nodes) => {
            const size = (nodes[i] as SVGTextElement).getBBox().width;
            maxLabelWidth = Math.max(maxLabelWidth, size);
          });
        });

      if (this.isSecondary) {
        yAxisElement = yAxisElement.attr(
          'transform',
          `translate(${chartWidth - maxLabelWidth - LinearAxisRenderer.TICK_SIZE}, 0 )`
        );
      }

      if (!this.hasSecondary) {
        // Remove the yAxis line for line charts and bar charts
        yAxisElement.call(g => g.select('.domain').remove());
      }
    }

    return {
      scale: y,
      width: maxLabelWidth + LinearAxisRenderer.TICK_SIZE,
      formatter,
    };
  }

  public renderX(
    width: number,
    height: number,
    limit?: number,
    inverted?: boolean
  ): { scale: d3.ScaleLinear<number, number> } {
    const x: d3.ScaleLinear<number, number> = d3.scaleLinear().rangeRound([0, width]);

    const xAxis = !this.isSecondary && this.hasSecondary ? d3.axisTop(x) : d3.axisBottom(x);
    const yTranslate: number = !this.isSecondary && this.hasSecondary ? 0 : height;

    const unit =
      (this.isSecondary ? this.config?.additionalValue?.unit : this.config?.value?.unit) ?? MeasureUnitCode.NONE;

    const unitInfo = unit instanceof MeasureUnitInfo ? unit : new MeasureUnitInfo(unit);

    const { step, minStep, maxStep, tickCount } = this.calcTickConfig(unitInfo.code, width, limit, inverted);

    x.domain([minStep, maxStep]).nice(tickCount);

    if (!this.config.xAxisConfig.hide) {
      const gContainerX = this.gElement
        .append('g')
        .attr('class', 'axis axis--x')
        .attr('transform', `translate(0, ${yTranslate})`);

      const tickSize = this.hasSecondary ? -LinearAxisRenderer.TICK_SIZE : -height;

      const formatter = this.createValueFormatter(
        unitInfo,
        step,
        maxStep,
        this.config.breakdownMode === 'fill-stacked'
      );

      xAxis.tickFormat((d: any) => formatter(d)).tickSize(tickSize);

      const xAxisContainer = gContainerX.call(xAxis);
      xAxisContainer.call(g => g.select('.domain').remove());

      const rotate = 60;
      let dx = 5;
      let textAnchor = 'end';

      if (!this.isSecondary && this.hasSecondary) {
        dx = -5;
        textAnchor = 'start';
      }

      xAxisContainer
        .selectAll('text')
        .attr('dx', -dx)
        .attr('transform', `rotate(-${rotate})`)
        .style('text-anchor', textAnchor);
    }
    return { scale: x };
  }

  private createValueFormatter(unitInfo: MeasureUnitInfo, step: number, maxStep: number, isFillStacked: boolean) {
    const unitConfig = MeasureUnitUtil.calcUnitConfig(maxStep, step);
    unitConfig.isDiff = !isFillStacked && !this.isSecondary && this.config.value?.formatConfig?.isDiff;

    const valueFormatterUnit = isFillStacked ? new MeasureUnitInfo(MeasureUnitCode.PERCENT) : unitInfo;

    if (valueFormatterUnit.code === MeasureUnitCode.PERCENT) {
      const decimalPart = step.toString().split('.')[1];
      unitConfig.decimalPlaces = Math.max(0, decimalPart ? decimalPart.length - 2 : 0);
    }

    return (d: number) => this.context.measureFormatter(d, valueFormatterUnit, unitConfig);
  }

  public calcAxisHeight(): number {
    if (this.config.xAxisConfig.hide) {
      return 5;
    }
    return this.config.rotateXLabels ? 70 : 50;
  }

  protected calcTickConfig(
    unitCode: string,
    availableSpace: number,
    limit?: number,
    inverted?: boolean
  ): { step: number; minStep: number; maxStep: number; tickCount: number } {
    const { min, max } = this.calcMinMax(limit, inverted);

    const tickCount = Math.max(2, Math.ceil(availableSpace / 60)) + 2;

    const step: number = d3.tickStep(min, max, tickCount);

    const minStep = Math.floor(min / step) * step;
    const maxStep = Math.ceil(max / step) * step;

    if (unitCode === 'QTY' && minStep === 0 && maxStep === 1) {
      return {
        step: 1,
        minStep,
        maxStep,
        tickCount: 1,
      };
    }
    return {
      step,
      minStep,
      maxStep,
      tickCount,
    };
  }

  private calcMinMax(limit?: number, inverted?: boolean): { min: number; max: number } {
    const values: number[] = [];

    if (this.config.breakdownMode === 'stacked') {
      const labelValues: Record<string, { pos: number; neg: number }> = {};

      this.config
        .data!.filter(array => array.type === ChartDataType.breakdown)
        .forEach(array => {
          const data = array.getData(limit, inverted);
          data.forEach(dataEle => {
            labelValues[dataEle.label!] = labelValues[dataEle.label!] ?? {
              pos: 0,
              neg: 0,
            };
            const val: { pos: number; neg: number } = labelValues[dataEle.label!];
            if (dataEle.value! < 0) {
              val.neg += dataEle.value!;
            } else {
              val.pos += dataEle.value!;
            }
          });
        });

      Object.values(labelValues).forEach(v => {
        values.push(v.pos);
        values.push(v.neg);
      });
    } else if (this.config.breakdownMode === 'fill-stacked') {
      return { min: 0, max: 1 };
    } else {
      this.config
        .data!.filter(array => {
          if (this.isSecondary) {
            return array.type === ChartDataType.secondary;
          }
          return !this.hasSecondary || array.type !== ChartDataType.secondary;
        })
        .filter(array => this.config.breakdownMode !== 'parallel' || array.type === ChartDataType.breakdown)
        .forEach(array =>
          array.getData(limit, inverted).forEach((dataEle: BandChartData) => {
            values.push(dataEle.value!);
            if (dataEle.upper) {
              values.push(dataEle.upper!);
            }
            if (dataEle.lower) {
              values.push(dataEle.lower!);
            }
          })
        );
    }

    if (!this.isSecondary && this.config.value?.max) {
      values.push(this.config.value!.max!);
    }

    if (this.isSecondary && this.config.additionalValue?.max) {
      values.push(this.config.additionalValue!.max!);
    }

    let min = Math.min(...values);
    if (min > 0) {
      min = 0;
    }
    let max = Math.max(...values);
    if (max < 0) {
      max = 0;
    }

    /**
     * in case of min max all values in the series are the same,
     * we need min & max to be different, otherwise we can't render a scale
     */
    if (min === max) {
      max += 1;
    }

    return { min, max };
  }
}
